import React, { useEffect, useState, useRef, useMemo } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import cn from 'classnames'
import { get } from 'lodash'

import GratuityHeader from '../components/GratuityHeader'
import styles from './CardReader.module.scss'
import VNConcessionsNavBar from '../components/VNConcessionsNavBar'
import ConfirmModalV2 from '../components/ConfirmModalV2'

import { ReactComponent as CreditCard } from '../assets/icons/NewCreditCard.svg'
import { ReactComponent as Discover } from '../assets/icons/discoverCard.svg'
import { ReactComponent as Amex } from '../assets/icons/amexCard.svg'
import { ReactComponent as Visa } from '../assets/icons/visaCard.svg'
import { ReactComponent as Master } from '../assets/icons/masterCardSm.svg'

import useGetIsMobileScreen from '../hooks/useGetIsMobileScreen'
import { useRemoteOrderTotalWithCallbacks } from '../hooks/useRemoteOrderTotal'
import useCloseFreeOrder from '../hooks/useCloseFreeOrder'
import useSpinner from '../context/hooks/useSpinner'

import { getCartLineItems, getTotal } from '../selectors/cart'
import { getBridgePaymentMethod, getPaymentType, getTipOnCardReader } from '../selectors/peripheral'
import { getIsSyncing } from '../VNSyncManager/Selectors'
import { getTipsEnabled } from '../selectors/menus'
import { getRemoteOrderTotal } from '../selectors/orderTotalRemote'
import { getOrderInProgress } from '../selectors/order'

import { INTERNAL_ORDER_STATES, isTabbed } from '../utils/orderStates'
import { cardReaderApproved, getCardDataMessage } from '../utils/orderUtils'
import { invoiceNumber as getInvoiceNumber } from '../utils/orderNumbers'
import PeripheralBridge from '../utils/peripheralBridge'
import { isOfflineApproval } from '../utils/offlineReplayStatuses'
import { nextPosOrderNumber } from '../utils/orderNumbers'
import { getDeviceSerial } from '../utils/deviceData'
import createDataDogLog from '../utils/datadog'
import { ToastManager } from '../utils/toastManager'
import { PAYMENT_TYPES } from '../utils/paymentTypes'
import { getTransactionBrandingConfig } from '../utils/buildSettings'
import { orderUuidFromOrderNumber } from '../utils/orderNumbers'

import {
  addPaymentToOrderInProgress,
  setIsOrderCancelled,
  setPaymentCompleted,
  startATab,
  startOfflineCardOrder,
  startOnlineCardOrder,
} from '../actions/order'

import { resetSafFullResponse, setBridgePaymentMethod } from '../actions/peripheral'

import { bridgePaymentMethods } from '../reducers/peripheral'
import { clearRecoveryData, setOrderDataForTransaction } from '../VNTransactions/ActionCreators'
import { getActiveAttendantId } from '../selectors/attendant'
import { setIsTabNameRequired } from '../VNTabs/ActionCreators'
import { setRoute } from '../VNRoutes/ActionCreators'
import { getHasRecoveryData } from '../VNTransactions/Selectors'
import { APPLICATION_STRINGS } from '../strings'
import VNLoader from '../VNLoader/components/VNLoader'
import useInvoiceNumbers from '../VNOrders/hooks/useInvoiceNumbers'

export const CardReaderReady = () => {
  let modalText = 'Pay with Card'
  let subText = 'All major credit cards accepted'

  return (
    <>
      <CreditCard className={styles.creditCard} />
      <p className={styles.cardReadyText}>{modalText}</p>
      <p className={styles.subText}>{subText}</p>
      <div className={styles.cardsContainer}>
        <Master className={styles.cards} />
        <Visa className={styles.cards} />
        <Discover className={styles.cards} />
        <Amex className={styles.cards} />
      </div>
    </>
  )
}

export const CardReaderErrorModal = ({ message, onCancel, onRetry }) => {
  return (
    <>
      <ConfirmModalV2
        headerText={APPLICATION_STRINGS.CARD_READER_STATUS}
        subtext={message}
        buttonOneText={'Cancel'}
        buttonTwoText={'Retry'}
        onButtonOneClick={onCancel}
        onButtonTwoClick={onRetry}
      />
    </>
  )
}

export const CardReaderBusy = ({ onCancel }) => {
  return (
    <>
      <p className={styles.paymentFailedSubtext}>{'Card Reader Is Busy Syncing Offline Orders'}</p>
      <button onClick={onCancel} className={styles.cancelButton}>
        cancel
      </button>
    </>
  )
}

const CardReader = ({
  bridgeMethod,
  onStartAuth,
  onSuccess,
  onFailureAuth,
  onOfflineSuccess,
  total,
  signature,
  selectedTipAmount,
  donationAmountInCents,
  deviceType,
  isSyncing,
  message,
  recoveryStatus = 'ready',
  addPaymentToOrder,
  clearSafResults,
  paymentId,
  tenderAmountInCents,
  offlineTotal,
  history,
  className,
  startNewTab,
  goPrevScreen,
  redirectPath,
  authTabs,
  shouldShowTipOnCardReader,
}) => {
  const classNames = cn(styles.cardReader, className)
  const dispatch = useDispatch()

  const [status, setStatus] = useState(recoveryStatus)
  const [isFreeAuthTab, setIsFreeAuthTab] = useState(false) // free tabs should auth to one dollar
  const [errorMessage, setErrorMessage] = useState(message ?? '')
  const orderInProgress = useSelector(getOrderInProgress)
  const isInRecoveryMode = useSelector(getHasRecoveryData)
  const [orderNumber] = useState(orderInProgress?.orderNumber ?? nextPosOrderNumber())
  const [uuid] = useState(orderUuidFromOrderNumber(orderNumber))

  // generate a new invoice number for transaction
  const { getInvoiceNumberFromOrder } = useInvoiceNumbers()
  const invoiceNumberFromOrder = getInvoiceNumberFromOrder()
  const invoiceNumber = useMemo(
    () => invoiceNumberFromOrder || getInvoiceNumber(orderNumber),
    [invoiceNumberFromOrder, orderNumber],
  )

  const [transactionApproved, setTransactionApproved] = useState(false)
  const [transactionError, setTransactionError] = useState(false)
  const [cardReaderInitialized, setCardReaderInitialized] = useState(false)
  const [transactionCancelled, setTransactionCancelled] = useState(false)
  const transactionCancelledRef = useRef(transactionCancelled)
  const isMobile = useGetIsMobileScreen()
  const isTabOrder = isTabbed(orderInProgress)
  const attendantId = useSelector((state) => getActiveAttendantId(state))
  const tipAmountInCents = selectedTipAmount || 0
  const { startSpinner, stopSpinner, showSpinner } = useSpinner()
  const { closeFreeOrder } = useCloseFreeOrder()

  const lineItems = useSelector((state) => getCartLineItems(state))
  // Set transaction data to state so we can pick this from state in
  // the event of a WIN Death.  Should be set as it's selected, not
  // passed via route state
  useEffect(() => {
    dispatch(
      setOrderDataForTransaction(
        orderInProgress?.uuid ?? uuid,
        orderNumber,
        paymentId,
        attendantId,
        tipAmountInCents,
        tenderAmountInCents,
        orderInProgress?.balanceDueInCents,
        orderInProgress?.payments,
        orderInProgress.hasTipApplied || !!orderInProgress.tipAmountInCents,
        signature ?? null,
        !!authTabs,
        lineItems,
        donationAmountInCents,
      ),
    )
  }, [
    attendantId,
    authTabs,
    dispatch,
    lineItems,
    orderNumber,
    paymentId,
    orderInProgress,
    signature,
    tipAmountInCents,
    tenderAmountInCents,
    uuid,
    donationAmountInCents,
  ])

  // If there is a redirect path, we handle it as a tabbed order
  const redirect = (data, orderNumber) => {
    if (!redirectPath) {
      history.push(`receipt/${uuid}`)
      return
    }

    if (authTabs) {
      startSpinner()
      dispatch(setIsTabNameRequired(true))
      history.push(redirectPath, {
        cardReaderData: data,
        paymentId,
        paymentType: PAYMENT_TYPES.CREDIT_CARD,
        orderNumber,
      })
      return
    }

    startSpinner()
    dispatch(setIsTabNameRequired(true))
    history.push(redirectPath)
  }

  // The single entry point for bridge registration on mount.
  const registerBridgeHandlers = (orderTotal) => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) {
      createDataDogLog('error', 'Card Reader - No Bridge Instance', { uuid })
      return
    }

    createDataDogLog('info', 'Card Reader - Registering Bridge Handlers', { uuid, totalInCents: orderTotal })
    // changes to handleResult need to also be implemented in:
    //   src/VNTransactions/actions/VNTransactionRecovery.js
    bridgeInstance.registerHandler('handleResult', function (data, callback) {
      // If handle result returns null or undefined, set error and abort transaction
      if (!data) {
        setStatus('error')
        setErrorMessage('Bad response.')
        createDataDogLog('error', 'Card Reader - Bad Response', { data, uuid, paymentId })
        return
      }

      // if true, abort due to transaction cancelled
      if (transactionCancelledRef.current) return

      setCardReaderInitialized(false)

      // The tip amount returned from the card reader will be in dollars.
      // This card reader tip handling is currently only for the E700 and thus isolated to this
      // screen's flow.
      // TODO (mkramerl): Update this as needed for other devices.
      let processedTipAmount = tipAmountInCents

      // when we receive '0.0' as tip from cardReader
      if (data?.tipAmount && parseFloat(data.tipAmount) > 0) {
        processedTipAmount = Math.round(data.tipAmount * 100)
      }

      const dataWithUuid = { ...data, orderNumber, uuid }
      const orderDetails = [dataWithUuid, signature, processedTipAmount, orderNumber, donationAmountInCents, invoiceNumber]

      if (cardReaderApproved(data)) {
        setTransactionApproved(true)

        // Only after confirming payment, when using "PayNow" in
        // BackBar, can we start a new tab with CC
        if (startNewTab) {
          const tabName = dataWithUuid?.cardholderName?.trim()
          dispatch(
            startATab({
              tabName,
              cardReaderData: dataWithUuid,
              paymentId,
              paymentType: PAYMENT_TYPES.CREDIT_CARD,
              printAuth: true,
              redirectPath: '/concession-order',
            }),
          )
          return
        }

        if (paymentId) {
          addPaymentToOrder(
            dataWithUuid,
            signature,
            processedTipAmount,
            tenderAmountInCents,
            paymentId,
            orderNumber,
            isOfflineApproval({ cardReaderData: data }),
            donationAmountInCents,
          )
        } else if (authTabs) {
          redirect(dataWithUuid, signature, processedTipAmount, tenderAmountInCents, tenderAmountInCents, orderNumber, isTabOrder)
        } else {
          isOfflineApproval({ cardReaderData: data }) ? onOfflineSuccess(...orderDetails) : onSuccess(...orderDetails)
          redirect()
        }
      } else {
        const message = getCardDataMessage(data)

        if (!authTabs && !isTabOrder) {
          onFailureAuth(...orderDetails)
        }

        setStatus('error')
        setErrorMessage(message)

        createDataDogLog('error', 'Card Reader - Card Reader Error', {
          data,
          uuid,
          paymentId,
          invoiceNumber: data?.invoiceNumber,
        })
      }

      var response = { message: data }
      callback(response)
    })

    bridgeInstance.registerHandler('errorTransaction', function (data, callback) {
      // if true, abort due to transaction cancelled
      if (transactionCancelledRef.current) return

      createDataDogLog('error', 'Card Reader - Error Transaction Event', { data, uuid })
      setCardReaderInitialized(false)

      const dataWithUuid = { ...data, uuid }
      const orderDetails = [dataWithUuid, signature, tipAmountInCents, orderNumber]
      const response = { message: dataWithUuid }

      if (!authTabs && !isTabOrder) {
        onFailureAuth(...orderDetails)
      }

      setStatus('error')
      setTransactionError(true)

      callback(response)
    })

    bridgeInstance.registerHandler('onTransactionRequestStarted', function (data, callback) {
      createDataDogLog('info', 'Card Reader - Transaction Request Started', { data, uuid })
    })
  }
  /**
   * Initiate the card reader with a transaction for a specific amount.
   * Tabbed orders, when
   *
   * @param {String} totalAsString
   * @returns
   */
  const initiateCardReader = (totalAsString, fromCallback = true) => {
    if (fromCallback && status === 'error') return

    let orderTotal = totalAsString
    // free tabs should auth to one dollar
    if (authTabs && totalAsString === '0.00') {
      setIsFreeAuthTab(true)
      orderTotal = '1.00'
    }

    // Don't initiate the card reader if the order is free.
    if (orderTotal === '0.00') return

    // Synchronously register the bridge handlers when the card reader is known to be being
    // initialized. This prevents erroneous bridge callbacks from being made by the APK before the
    // card reader is actually ready. (e.g. a cancellation attempt before a transaction has
    // actually started)
    registerBridgeHandlers(orderTotal)

    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return

    if (!orderTotal) {
      ToastManager.error('Reader initialization failed : Invalid total. Please go back and try your transaction again.', { autoClose: false })
      return
    }

    const deviceSerial = getDeviceSerial()
    setCardReaderInitialized(true)

    // contract should match com/venuenext/poscore/model/Transaction.kt
    bridgeInstance.call(bridgeMethod, {
      uuid: uuid,
      orderNumber,
      hRef: invoiceNumber,
      amount: orderTotal,
      tipAmount: (tipAmountInCents / 100).toFixed(2),
      tipEnabled: shouldShowTipOnCardReader,
      ...getTransactionBrandingConfig(),
      deviceSerial: deviceSerial,
    })

    createDataDogLog('info', 'Card Reader - Execute Sale Request Made', {
      orderUuid: uuid,
      orderNumber,
      invoiceNumber: invoiceNumber,
      transactionAmount: orderTotal,
    })
  }

  // TODO (JEFF) Now that totaling happens from the cart, we should no longer need totaling logic in this component
  const [loadRemoteOrderTotal, { data: remoteOrderTotal, loading, success, failed }] = useRemoteOrderTotalWithCallbacks(
    initiateCardReader,
    offlineTotal || total,
    tipAmountInCents,
    donationAmountInCents,
  )

  const showProcessing = isMobile && deviceType === 'pax'
  const totalInCents = paymentId ? tenderAmountInCents : (remoteOrderTotal?.totalAmountInCents ?? (offlineTotal || total))
  const totalDisplay = `$${((totalInCents + donationAmountInCents) / 100).toFixed(2)}`
  const tip = `$${(tipAmountInCents / 100).toFixed(2)}`
  const grandTotalInCents = totalInCents + tipAmountInCents + donationAmountInCents
  const grandTotal = `$${(grandTotalInCents / 100).toFixed(2)}`
  const formattedTotal = (grandTotalInCents / 100).toFixed(2)
  const totalInCentsWithTips = totalInCents + tipAmountInCents
  const noPaymentRequired = totalInCentsWithTips === 0

  let primaryText = grandTotal ?? '-'
  let secondaryText = `${totalDisplay} + ${tip} tip`

  const performPreauthSubmission = () => {
    // do not attempt to create a new order if the tabbed order already exists
    if (orderInProgress?.wasCreatedInStadium) return
    // If the order is a tabbed order, we don't need to create a new order.
    if (authTabs || orderInProgress?.isTab) return

    const dataWithUuid = { orderNumber, uuid }
    const orderDetails = [
      dataWithUuid,
      signature,
      tipAmountInCents,
      orderNumber,
      tenderAmountInCents,
      donationAmountInCents,
      invoiceNumber,
    ]

    onStartAuth(...orderDetails)
  }

  const onCancel = (shouldAttemptCancelation = true, reason) => {
    setTransactionCancelled(true)

    createDataDogLog('info', 'Card Reader - Cancel', {
      uuid,
      orderNumber,
      invoiceNumber: invoiceNumber,
      amountInCents: totalInCentsWithTips,
    })

    const bridgeInstance = PeripheralBridge.getBridge()

    if (!bridgeInstance || transactionError || !shouldAttemptCancelation) {
      setTransactionError(false)
    } else {
      bridgeInstance.call('cancelTransactionAttempt')
    }

    if (!authTabs && !isTabOrder) {
      // this will prevent the TenderSelect screen from re-totalling a Pay Now Order
      dispatch(setIsOrderCancelled(true))
      dispatch(setPaymentCompleted({ orderUuid: remoteOrderTotal?.uuid, paymentCompleted: false }))
    }

    // there is no "previous" screen to go back to after a crash
    if (isInRecoveryMode) {
      dispatch(clearRecoveryData())
      dispatch(setRoute('/concession-order'))
      return
    }

    goPrevScreen()
  }

  const onGoBack = () => {
    onCancel(false, 'User cancelled order by selecting Go Back')
  }

  const onRetry = () => {
    createDataDogLog('info', 'Card Reader - Retry', {
      uuid,
      orderNumber,
      invoiceNumber: invoiceNumber,
      amountInCents: totalInCentsWithTips,
    })

    if (isInRecoveryMode) {
      dispatch(clearRecoveryData())
    }

    const bridgeInstance = PeripheralBridge.getBridge()

    if (!bridgeInstance || transactionError) {
      setTransactionError(false)
      setStatus('ready')
      initiateCardReader(formattedTotal, false)
      return
    }

    setStatus('ready')
    setErrorMessage()
    initiateCardReader(formattedTotal, false)
  }

  const shutDownCardReader = () => {
    createDataDogLog('info', 'Card Reader - Shut Down', {
      uuid,
      orderNumber,
      invoiceNumber: invoiceNumber,
      amountInCents: totalInCentsWithTips,
    })

    const bridgeInstance = PeripheralBridge.getBridge()

    if (bridgeInstance) bridgeInstance.call('cancelTransactionAttempt')
  }

  useEffect(() => {
    clearSafResults()
    performPreauthSubmission()
    registerBridgeHandlers()

    if (showSpinner) {
      stopSpinner()
    }

    if (!paymentId) {
      loadRemoteOrderTotal()
    } else {
      // For split orders, the remote totals doesn't need to be waited on.
      initiateCardReader(formattedTotal, false)
    }

    const handleBeforeUnload = (event) => {
      createDataDogLog('error', 'Card Reader - Screen Reloaded Unexpectedly', {
        uuid,
        orderNumber,
        invoiceNumber: invoiceNumber,
        amountInCents: totalInCentsWithTips,
        bridgeActive: cardReaderInitialized,
      })
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      dispatch(setBridgePaymentMethod(bridgePaymentMethods.executeSaleRequest))
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (authTabs) return
    if (noPaymentRequired && !loading && (success || failed || isTabOrder)) {
      closeFreeOrder(uuid, isTabOrder)
    }
  }, [authTabs, loading, success, failed, noPaymentRequired, orderNumber, closeFreeOrder, uuid, isTabOrder])

  useEffect(() => {
    if (transactionError) {
      setStatus('error')
    }
  }, [transactionError])

  useEffect(() => {
    transactionCancelledRef.current = transactionCancelled
  }, [transactionCancelled])

  let content = showProcessing ? <VNLoader standalone /> : <CardReaderReady deviceType={deviceType} safIsUploading={isSyncing} />

  if (status === 'error') {
    content = (
      <CardReaderErrorModal
        message={errorMessage}
        onCancel={() => onCancel(true, 'User cancelled order transaction from card reader ERROR modal')}
        onRetry={onRetry}
        deviceType={deviceType}
      />
    )
  }

  if (status === 'busy') {
    content = <CardReaderBusy onCancel={() => onCancel(true, 'User cancelled order transaction from card reader BUSY modal')} />
  }

  useEffect(() => {
    return () => {
      if (!transactionApproved) {
        shutDownCardReader()
      }
    }
    // eslint-disable-next-line
  }, [transactionApproved])

  if (noPaymentRequired && !isFreeAuthTab) {
    return <VNLoader />
  }

  return (
    <div className={cn(classNames)}>
      <VNConcessionsNavBar
        textDisplay={isMobile && primaryText}
        onClick={onGoBack}
        cardReaderScreen={true}
        cardReaderInitialized={cardReaderInitialized}
      />
      <GratuityHeader primaryText={!isMobile && primaryText} secondaryText={secondaryText} />
      <div className={styles.contentContainer}>{content}</div>
    </div>
  )
}

const mapStateToProps = (state, ownProps) => {
  const total = getTotal(state)
  const recoveryStatus = ownProps?.history?.location?.state?.recoveryStatus
  const redirectPath = ownProps?.history?.location?.state?.redirectPath ?? ''
  const authTabs = ownProps?.history?.location?.state?.authTabs ?? ''
  const offlineTotal = ownProps?.history?.location?.state?.offlineTotal
  const paymentId = ownProps?.history?.location?.state?.paymentId
  const startNewTab = ownProps?.history?.location?.state?.startNewTab
  let tenderAmountInCents = ownProps?.history?.location?.state?.tenderAmountInCents ?? 0
  const selectedTipAmount = ownProps?.history?.location?.state?.selectedTipAmount
  const donationAmountInCents = ownProps?.history?.location?.state?.donationAmountInCents ?? 0
  const signature = ownProps?.history?.location?.state?.signature
  const message = ownProps?.history?.location?.state?.message
  const remoteOrderTotal = getRemoteOrderTotal(state)
  const orderUuid = remoteOrderTotal?.uuid

  const shouldShowTipOnCardReader = getTipOnCardReader(state) && getTipsEnabled(state)
  const bridgeMethod = getBridgePaymentMethod(state)
  const deviceType = getPaymentType(state)
  const isSyncing = getIsSyncing(state)

  // [T36260] free tabs should auth to one dollar
  if (authTabs && tenderAmountInCents === 0) {
    tenderAmountInCents = 100
  }

  return {
    offlineTotal,
    paymentId,
    orderUuid,
    tenderAmountInCents,
    selectedTipAmount,
    donationAmountInCents,
    message,
    shouldShowTipOnCardReader,
    bridgeMethod,
    deviceType,
    isSyncing,
    signature,
    startNewTab,
    total,
    redirectPath,
    recoveryStatus,
    authTabs,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const goPrevScreen = get(ownProps, 'history.goBack', () => {})

  return {
    goPrevScreen: () => goPrevScreen(),
    onClose: () => ownProps?.history?.location?.state?.onClose,
    onStartAuth: (
      cardReaderData,
      signature,
      tipAmountInCents,
      orderNumber,
      tenderAmountInCents,
      charityAmountInCents,
      invoiceNumber,
    ) => {
      dispatch(
        startOnlineCardOrder({
          cardReaderData,
          signature,
          tipAmountInCents,
          orderNumber,
          tenderAmountInCents,
          orderState: INTERNAL_ORDER_STATES.PREAUTH,
          charityAmountInCents,
          invoiceNumber,
        }),
      )
    },
    onFailureAuth: (cardReaderData, signature, tipAmountInCents, orderNumber) => {
      dispatch(
        startOnlineCardOrder({
          cardReaderData,
          signature,
          tipAmountInCents,
          orderNumber,
          orderState: INTERNAL_ORDER_STATES.FAILED,
        }),
      )
    },
    onOfflineSuccess: (cardReaderData, signature, tipAmountInCents, orderNumber, charityAmountInCents, invoiceNumber) => {
      dispatch(
        startOfflineCardOrder({ cardReaderData, signature, tipAmountInCents, orderNumber, charityAmountInCents, invoiceNumber }),
      )
    },
    onSuccess: (cardReaderData, signature, tipAmountInCents, orderNumber, charityAmountInCents, invoiceNumber) => {
      dispatch(
        startOnlineCardOrder({ cardReaderData, signature, tipAmountInCents, orderNumber, charityAmountInCents, invoiceNumber }),
      )
    },
    addPaymentToOrder: (
      cardReaderData,
      signature,
      tipAmountInCents,
      tenderAmountInCents,
      paymentId,
      orderNumber,
      isApprovedOffline,
      charityAmountInCents,
    ) => {
      dispatch(
        addPaymentToOrderInProgress({
          cardReaderData,
          signature,
          orderNumber,
          tipAmountInCents,
          tenderAmountInCents,
          paymentId,
          paymentType: PAYMENT_TYPES.CREDIT_CARD,
          isApprovedOffline,
          charityAmountInCents,
        }),
      )
    },
    clearSafResults: () => {
      // Clear the SAF results so that the next time the user goes to the card reader screen, the
      // results are not stale.
      dispatch(resetSafFullResponse())
    },
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CardReader))
